/**
 * @file api-picking.ts
 * @description Admin API-клиент сборочного листа (phase19.14, W13).
 *
 * Зеркалит `apps/api/src/wms/picking/picking-serializer.ts`: задание
 * обезличено — ни клиента, ни адреса, ни цен. Кладовщику отдаётся только
 * способ получения (для выбора упаковки), состав и адреса ячеек.
 *
 * Количества приходят строками (`Decimal` на сервере) — сравнивать их надо
 * через `Number(...)`, а не лексикографически.
 */

import {
  apiDownload,
  apiFetch,
  apiFetchWithMeta,
  type ApiFileResponse,
} from "./api-client";

// ─── Types ────────────────────────────────────────────────────────────────────

export type PickingTaskStatus =
  | "NEW"
  | "IN_PROGRESS"
  | "DONE"
  | "SHORTAGE"
  | "CANCELLED";

export type PickingShortageReason =
  | "NOT_FOUND"
  | "DAMAGED"
  | "EXPIRED"
  | "LOT_MISMATCH"
  | "OTHER";

/** Способ получения без персональных данных (см. `resolveDeliveryMethod`). */
export type PickingDeliveryMethod =
  | "PICKUP"
  | "SDEK"
  | "POCHTA"
  | "YANDEX"
  | "PEK"
  | "OZON"
  | "FIVE_POST"
  | "CUSTOM"
  | "AGREEMENT"
  | null;

/** Партия в строке задания. */
export interface PickingLotRef {
  id: string;
  batchNumber: string;
  expiresAt: string | null;
}

/**
 * Строка состава задания в очереди (phase19.24 §4): кладовщик выбирает
 * задание по товару, а не по номеру. Сервер отдаёт до трёх строк в порядке
 * обхода склада.
 */
export interface PickingTaskItemPreviewDto {
  /** Название товара с фасовкой — как в карточке задания. */
  name: string;
  /** План по строке (Decimal строкой). */
  qty: string;
  /** Сокращение единицы фасовки: «г», «шт», «мл». */
  unit: string;
}

/** Карточка задания в очереди (обезличенная). */
export interface PickingTaskListItemDto {
  id: string;
  number: string;
  orderId: string;
  orderNumber: string;
  status: PickingTaskStatus;
  warehouse: { id: string; name: string; code: string };
  assignee: { id: string; displayName: string | null } | null;
  deliveryMethod: PickingDeliveryMethod;
  itemsCount: number;
  pickedCount: number;
  unitsTotal: string;
  unitsPicked: string;
  hasShortage: boolean;
  /**
   * Сколько строк закрыто недостачей. Поле пришло вместе с журналом заданий,
   * старый сервер его не шлёт — читаем через `?? 0`.
   */
  shortageItemsCount?: number;
  /**
   * Первые строки задания в порядке обхода склада (phase19.24 §4). Сервер
   * отдаёт поле всегда; пустой массив значит «строк в задании нет».
   */
  itemsPreview: PickingTaskItemPreviewDto[];
  /** Всего строк в задании — по нему очередь считает подпись «+N ещё». */
  itemsTotal: number;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** Строка задания: что брать, сколько и из какой ячейки. */
export interface PickingTaskItemDto {
  id: string;
  skuId: string;
  skuCode: string;
  sku: {
    id: string;
    code: string;
    displayName: string | null;
    productName: string;
    imageUrl: string | null;
  };
  binCode: string | null;
  binSortKey: number | null;
  /**
   * Ячейка размещения фасовки по умолчанию (phase19.24 §3.1) — подсказка «где
   * обычно лежит», когда у строки нет адреса партии (`binCode = null`).
   */
  defaultBinCode: string | null;
  /** Физический остаток фасовки на складе задания (все партии, Decimal строкой). */
  stockOnHand: string;
  /**
   * Доступно под эту строку: остаток минус брони ЧУЖИХ заказов. Бронь своего
   * заказа не вычитается — кладовщик идёт собирать именно её.
   */
  stockAvailable: string;
  plannedLot: PickingLotRef | null;
  pickedLot: PickingLotRef | null;
  qtyPlanned: string;
  qtyPicked: string;
  /**
   * Сокращение единицы фасовки: «г», «мл», «шт» (phase19.24, довесок шага 4).
   * Количество в интерфейсе всегда печатается с единицей — «2 / 3» без неё
   * кладовщик читал как штуки, даже когда в строке граммы.
   */
  unit: string;
  shortageQty: string;
  shortageReason: PickingShortageReason | null;
  note: string | null;
  pickedAt: string | null;
  reservationId: string | null;
  /**
   * Есть ли под строкой бронь склада (phase19.23). Строка без брони займёт
   * товар только при проведении задания — UI обязан об этом предупредить.
   * Поле опционально: старый сервер его не шлёт, тогда пометки нет.
   */
  hasReservation?: boolean;
}

/**
 * Почему состав задания выглядит так, как выглядит (phase19.23–19.24, зеркало
 * `picking-serializer.ts`): «услуги/доставка», «строки потерялись» и «позиция
 * заказа без фасовки» требуют от кладовщика разных действий.
 */
export type PickingItemsSource =
  | "items"
  | "services_only"
  | "empty"
  | "unlinked";

/**
 * Позиция заказа без фасовки (OQ-50): собрать её нельзя, чинится правкой
 * ЗАКАЗА, а не пересборкой задания. Сервер блокирует проведение (409
 * `picking_item_unlinked`), UI обязан объяснить это заранее.
 */
export interface PickingUnlinkedItemDto {
  orderItemId: string;
  name: string;
  skuCode: string | null;
}

/** Задание целиком. Строки уже отсортированы сервером по маршруту обхода. */
export interface PickingTaskDetailDto extends PickingTaskListItemDto {
  shortageNote: string | null;
  items: PickingTaskItemDto[];
  /** Опционально: старый сервер поля не отдаёт — считаем «строки есть». */
  itemsSource?: PickingItemsSource;
  /** Строк без брони: резерв встанет только при проведении задания. */
  itemsWithoutReservation?: number;
  /** Позиции заказа без фасовки; пусто — состав в порядке (phase19.24, OQ-50). */
  unlinkedItems: PickingUnlinkedItemDto[];
  /**
   * Можно ли пересобрать строки по резервам заказа прямо сейчас (phase19.23).
   * Считает сервер: NEW/SHORTAGE всегда, IN_PROGRESS — только если строк нет.
   * Поле опционально: старый сервер его не шлёт, тогда правило берём из
   * статуса (`canRebuildTask`).
   */
  canRebuild?: boolean;
}

/** Терминальные статусы: только они попадают в журнал заданий. */
export type PickingHistoryStatus = "DONE" | "CANCELLED";

/** Страница журнала: серверная пагинация, `total` приходит в `meta`. */
export interface PickingHistoryPage {
  items: PickingTaskListItemDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** Партия в предупреждении сверки (phase19.22, W17-9c). */
export interface PickingScanLotRef {
  lotId: string;
  code: string;
  batchNumber: string;
  expiresAt: string | null;
}

/**
 * Результат скана: ячейка (навигация) либо строка задания (отметка +1).
 * phase19.22: этикетка с ДРУГОЙ партией того же товара строку не отмечает —
 * `marked: false` и `warning.lot_mismatch`; повтор с `confirmLotMismatch` и
 * причиной отмечает с заменой партии (переходный период W17-9c).
 */
export type PickingScanResult =
  | {
      kind: "bin";
      bin: { id: string; code: string; label: string | null; sortKey: number };
    }
  | {
      kind: "item";
      task: PickingTaskDetailDto;
      itemId: string;
      marked?: boolean;
      warning?: { code: "lot_mismatch"; expected: PickingScanLotRef; scanned: PickingScanLotRef };
    };

/** Параметры повторного скана (phase19.22, W17-9c, W14-9). */
export interface PickingScanOptions {
  /** Подтвердить замену партии строки — нужна причина. */
  confirmLotMismatch?: boolean;
  reason?: string;
  /** Адрес, подтверждённый сканом `LOC:` (`requireBinScanOnPick`). */
  confirmedBinCode?: string;
}

// ─── Endpoints ────────────────────────────────────────────────────────────────

export const pickingApi = {
  /** Очередь заданий. Без `status` сервер отдаёт только живые + недостачи. */
  list: (params?: {
    status?: PickingTaskStatus;
    mine?: boolean;
    warehouseId?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.mine) q.set("mine", "true");
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    const qs = q.toString();
    return apiFetch<PickingTaskListItemDto[]>(
      `/admin/wms/picking${qs ? `?${qs}` : ""}`,
    );
  },
  /**
   * Журнал проведённых и отменённых заданий (`view=history`).
   *
   * Отдельный вызов, а не фильтр очереди: очередь грузится целиком и живёт в
   * памяти, журнал растёт бесконечно и листается страницами по `finishedAt`.
   */
  history: async (params: {
    status?: PickingHistoryStatus;
    warehouseId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PickingHistoryPage> => {
    const q = new URLSearchParams({ view: "history" });
    if (params.status) q.set("status", params.status);
    if (params.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params.page) q.set("page", String(params.page));
    if (params.pageSize) q.set("pageSize", String(params.pageSize));
    const { data, meta } = await apiFetchWithMeta<
      PickingTaskListItemDto[],
      { total: number; page: number; pageSize: number } | undefined
    >(`/admin/wms/picking?${q.toString()}`);
    return {
      items: data ?? [],
      total: meta?.total ?? data?.length ?? 0,
      page: meta?.page ?? params.page ?? 1,
      pageSize: meta?.pageSize ?? params.pageSize ?? 20,
    };
  },
  findOne: (id: string) =>
    apiFetch<PickingTaskDetailDto>(`/admin/wms/picking/${id}`),
  /** Взять в работу. 409 `picking_task_claimed` — задание уже у другого. */
  claim: (id: string) =>
    apiFetch<PickingTaskDetailDto>(`/admin/wms/picking/${id}/claim`, {
      method: "POST",
    }),
  /**
   * Освободить чужое задание / вернуть закрытое в очередь (админ склада).
   * Сервер сохраняет собранные количества и снимает отметки недостачи.
   * 422 `picking_order_closed` — заказ отменён или закрыт.
   */
  release: (id: string) =>
    apiFetch<PickingTaskDetailDto>(`/admin/wms/picking/${id}/release`, {
      method: "POST",
    }),
  /**
   * Пересобрать строки задания по текущим резервам заказа (SHOP_WMS_MANAGE,
   * статусы NEW/SHORTAGE). Лечит расхождение `picking_reservations_unassigned`
   * после правки состава заказа; собранные количества сохраняются.
   */
  rebuild: (id: string) =>
    apiFetch<PickingTaskDetailDto>(`/admin/wms/picking/${id}/rebuild`, {
      method: "POST",
    }),
  cancel: (id: string, note?: string) =>
    apiFetch<PickingTaskDetailDto>(`/admin/wms/picking/${id}/cancel`, {
      method: "POST",
      json: { note },
    }),
  /** Скан артикула, этикетки склада (`SKU:`/`LOT:`) либо `LOC:<адрес ячейки>`. */
  scan: (id: string, code: string, options: PickingScanOptions = {}) =>
    apiFetch<PickingScanResult>(`/admin/wms/picking/${id}/scan`, {
      method: "POST",
      json: { code, ...options },
    }),
  /** Ручная отметка количества; `pickedLotId: null` — вернуться к плановой. */
  pickItem: (
    id: string,
    itemId: string,
    input: {
      qtyPicked: number;
      pickedLotId?: string | null;
      /** phase19.22 (W14-9) — адрес, подтверждённый сканом ячейки. */
      confirmedBinCode?: string;
    },
  ) =>
    apiFetch<PickingTaskDetailDto>(
      `/admin/wms/picking/${id}/items/${itemId}/pick`,
      { method: "POST", json: input },
    ),
  /**
   * Недостача по строке. `reason` обязателен только при `shortageQty > 0` —
   * при нуле сервер снимает отметку и причина не нужна (см. `clearItemShortage`).
   */
  itemShortage: (
    id: string,
    itemId: string,
    input: {
      shortageQty: number;
      reason?: PickingShortageReason;
      note?: string;
    },
  ) =>
    apiFetch<PickingTaskDetailDto>(
      `/admin/wms/picking/${id}/items/${itemId}/shortage`,
      { method: "POST", json: input },
    ),
  /**
   * Снять отметку недостачи: тот же вход с `shortageQty: 0` — у одного
   * состояния строки не должно быть двух ручек с разными правами.
   * 409 `picking_shortage_declared` — недостача уже оформлена по заданию,
   * строки правятся только после возврата задания в очередь.
   */
  clearItemShortage: (id: string, itemId: string) =>
    apiFetch<PickingTaskDetailDto>(
      `/admin/wms/picking/${id}/items/${itemId}/shortage`,
      { method: "POST", json: { shortageQty: 0 } },
    ),
  /** Провести сборку — заказ уходит в «Собран», остаток списывается. */
  complete: (id: string) =>
    apiFetch<PickingTaskDetailDto>(`/admin/wms/picking/${id}/complete`, {
      method: "POST",
    }),
  /** Исход «недостача»: заказ остаётся на сборке, решает менеджер. */
  shortage: (id: string, note: string) =>
    apiFetch<PickingTaskDetailDto>(`/admin/wms/picking/${id}/shortage`, {
      method: "POST",
      json: { note },
    }),
  /** Блок «Сборка» в карточке заказа; null — задания ещё не было. */
  forOrder: (orderId: string) =>
    apiFetch<PickingTaskDetailDto | null>(`/admin/orders/${orderId}/picking`),
  /** Лист комплектации (PDF) — печатают из админки, с телефона не нужен. */
  printPickList: (id: string, number: string): Promise<ApiFileResponse> =>
    apiDownload(
      `/admin/wms/picking/${id}/pick-list.pdf`,
      { headers: { Accept: "application/pdf" } },
      `pick-list-${number}.pdf`,
    ),
};

// ─── Labels и хелперы (RU) ────────────────────────────────────────────────────

export const PICKING_STATUS_LABEL: Record<PickingTaskStatus, string> = {
  NEW: "Новое",
  IN_PROGRESS: "В работе",
  // «Закрыто», а не «Собрано»: «собрано» в интерфейсе занято метрикой
  // прогресса («собрано 2 из 3 позиций») — phase19.24.
  DONE: "Закрыто",
  SHORTAGE: "Недостача",
  CANCELLED: "Отменено",
};

// Тон статуса переехал в `components/wms/picking/PickingStatusBadge.tsx`
// (реестр `STATUS_TONE` поверх общего `DocumentStatusBadge`) — phase19.24.

export const PICKING_SHORTAGE_REASON_LABEL: Record<
  PickingShortageReason,
  string
> = {
  NOT_FOUND: "Не найдено на месте",
  DAMAGED: "Повреждено",
  EXPIRED: "Брак / срок",
  LOT_MISMATCH: "Не та партия",
  OTHER: "Другое",
};

/** Порядок причин в диалоге недостачи — как в макете шага 0. */
export const PICKING_SHORTAGE_REASONS: PickingShortageReason[] = [
  "NOT_FOUND",
  "DAMAGED",
  "EXPIRED",
  "LOT_MISMATCH",
  "OTHER",
];

/** Комментарий обязателен только для «Другое» (зеркало `picking-rules.ts`). */
export function isShortageNoteRequired(
  reason: PickingShortageReason,
): boolean {
  return reason === "OTHER";
}

export const PICKING_DELIVERY_LABEL: Record<string, string> = {
  PICKUP: "Самовывоз",
  SDEK: "СДЭК",
  POCHTA: "Почта России",
  YANDEX: "Яндекс Доставка",
  PEK: "ПЭК",
  OZON: "Ozon Rocket",
  FIVE_POST: "5Post",
  CUSTOM: "Своя доставка",
  AGREEMENT: "По договорённости",
};

/** Подпись способа получения; способ ещё не выбран — «Способ не выбран». */
export function formatDeliveryMethod(method: PickingDeliveryMethod): string {
  if (!method) return "Способ не выбран";
  return PICKING_DELIVERY_LABEL[method] ?? method;
}

/** Название позиции: «Иван-чай гранулированный — 50 г». */
export function formatPickItemName(item: PickingTaskItemDto): string {
  const product = item.sku.productName.trim();
  const variant = item.sku.displayName?.trim() ?? "";
  if (!variant || variant === product) return product || item.skuCode;
  return `${product} — ${variant}`;
}

/** Строка собрана полностью (кнопка «+» гаснет, карточка сворачивается). */
export function isItemPicked(item: PickingTaskItemDto): boolean {
  return Number(item.qtyPicked) >= Number(item.qtyPlanned);
}

/** Прогресс задания в процентах для полосы (0..100). */
export function pickingProgressPercent(
  task: Pick<PickingTaskListItemDto, "itemsCount" | "pickedCount">,
): number {
  if (task.itemsCount <= 0) return 100;
  return Math.round((task.pickedCount / task.itemsCount) * 100);
}

/** Задание закрыто: в журнале показываем только такие. */
export function isTerminalPickingTask(status: PickingTaskStatus): boolean {
  return status === "DONE" || status === "CANCELLED";
}

/** Задание ещё можно отмечать и проводить. */
export function isActivePickingTask(status: PickingTaskStatus): boolean {
  return status === "NEW" || status === "IN_PROGRESS";
}

/**
 * Задание можно пересобрать по текущим резервам заказа: строки ещё не
 * проведены и не отменены (зеркало правил `POST /picking/:id/rebuild`).
 */
export function canRebuildPickingTask(status: PickingTaskStatus): boolean {
  return status === "NEW" || status === "SHORTAGE";
}

/**
 * Доступность пересборки для конкретного задания.
 *
 * Источник правды — серверный флаг `canRebuild`: он знает про «задание в
 * работе, но строк ноль» (тупик, из которого кладовщик не выбирался). Расчёт
 * по статусу оставлен запасным вариантом для старого сервера.
 */
export function canRebuildTask(task: PickingTaskDetailDto): boolean {
  return task.canRebuild ?? canRebuildPickingTask(task.status);
}

/** Срок годности партии в коротком виде: «до 17.05.2027». */
export function formatLotExpiry(lot: PickingLotRef | null): string | null {
  if (!lot?.expiresAt) return null;
  return `до ${new Date(lot.expiresAt).toLocaleDateString("ru-RU")}`;
}
